import { defaultBillingAccountId } from "@/mock-api/data/billingAccountIds.ts";
import type { AdminBillingAccountStatusEvent } from "@warrenio/api-spec/spec.oats.gen";

const accountCreated = 1679595484;
const day = 24 * 60 * 60;

/** Owner of the default billing account */
const ownerUserId = 136;
const adminUserId = 9;

let nextId = 3001;

function event(
    daysAfterCreation: number,
    fields: Omit<AdminBillingAccountStatusEvent, "id" | "billing_account_id" | "created_at">,
): AdminBillingAccountStatusEvent {
    return {
        id: nextId++,
        billing_account_id: defaultBillingAccountId,
        created_at: accountCreated + daysAfterCreation * day,
        ...fields,
    };
}

const user = { actor_type: "USER", acting_user_id: ownerUserId } as const;
const admin = { actor_type: "ADMINISTRATOR", acting_user_id: adminUserId } as const;
const system = { actor_type: "SYSTEM" } as const;

/** Status history of the default billing account (more than one page). Every other billing account has no history. */
const chronologicalEvents: AdminBillingAccountStatusEvent[] = [
    event(0, {
        ...user,
        event_type: "RESTRICTION_LEVEL_CHANGED",
        old_value: "CLEAR",
        new_value: "FROZEN",
        reason_code: "NO_VALID_PAYMENT_METHOD",
        trigger: "account_create",
    }),
    event(0, {
        ...user,
        event_type: "RESTRICTION_LEVEL_CHANGED",
        old_value: "FROZEN",
        new_value: "CLEAR",
        reason_code: "POST_PAYMENT_DEFAULT",
        trigger: "payment_method",
        ongoing_balance: 0,
    }),
    event(3, {
        ...system,
        event_type: "BALANCE_BECAME_NEGATIVE",
        new_value: "-0.120000",
        trigger: "charging_subtotal",
        ongoing_balance: -0.12,
    }),
    event(31, {
        ...admin,
        event_type: "BALANCE_BECAME_POSITIVE",
        old_value: "-4.090000",
        new_value: "95.910000",
        trigger: "admin_credit",
        ongoing_balance: 95.91,
    }),
    event(62, {
        ...system,
        event_type: "BALANCE_BECAME_NEGATIVE",
        old_value: "0.350000",
        new_value: "-1.200000",
        trigger: "charging_subtotal",
        ongoing_balance: -1.2,
    }),
    event(76, {
        ...system,
        event_type: "RESTRICTION_LEVEL_CHANGED",
        old_value: "CLEAR",
        new_value: "FROZEN",
        reason_code: "NEGATIVE_BALANCE_DAYS",
        trigger: "charging_subtotal",
        ongoing_balance: -18.54,
        details: { days: 14, threshold: 14 },
    }),
    event(78, {
        ...user,
        event_type: "BALANCE_BECAME_POSITIVE",
        old_value: "-18.540000",
        new_value: "1.460000",
        trigger: "invoice_payment",
        ongoing_balance: 1.46,
    }),
    event(78, {
        ...user,
        event_type: "RESTRICTION_LEVEL_CHANGED",
        old_value: "FROZEN",
        new_value: "CLEAR",
        reason_code: "POST_PAYMENT_DEFAULT",
        trigger: "invoice_payment",
        ongoing_balance: 1.46,
    }),
    event(90, {
        ...admin,
        event_type: "SUSPENDED",
        old_value: "ACTIVE",
        new_value: "SUSPENDED",
        trigger: "admin_suspend",
        ongoing_balance: 0.85,
    }),
    event(90, {
        ...admin,
        event_type: "RESTRICTION_LEVEL_CHANGED",
        old_value: "CLEAR",
        new_value: "FROZEN",
        reason_code: "ACCOUNT_SUSPENDED",
        trigger: "admin_suspend",
        ongoing_balance: 0.85,
    }),
    event(90, {
        ...admin,
        event_type: "SUSPEND_REASON_CHANGED",
        new_value: "Suspicious activity",
        trigger: "admin_suspend_reason",
        ongoing_balance: 0.85,
    }),
    event(91, {
        ...admin,
        event_type: "SUSPEND_REASON_CHANGED",
        old_value: "Suspicious activity",
        new_value: "Unpaid invoices, please contact support",
        trigger: "admin_suspend_reason",
        ongoing_balance: 0.85,
    }),
    event(95, {
        ...admin,
        event_type: "UNSUSPENDED",
        old_value: "SUSPENDED",
        new_value: "ACTIVE",
        trigger: "admin_unsuspend",
        ongoing_balance: 0.85,
    }),
    event(95, {
        ...admin,
        event_type: "SUSPEND_REASON_CHANGED",
        old_value: "Unpaid invoices, please contact support",
        trigger: "admin_unsuspend",
        ongoing_balance: 0.85,
    }),
    event(95, {
        ...admin,
        event_type: "RESTRICTION_LEVEL_CHANGED",
        old_value: "FROZEN",
        new_value: "CLEAR",
        reason_code: "POST_PAYMENT_DEFAULT",
        trigger: "admin_unsuspend",
        ongoing_balance: 0.85,
    }),
    event(120, {
        ...admin,
        event_type: "RESTRICTION_LEVEL_CHANGED",
        old_value: "CLEAR",
        new_value: "LIMITED",
        reason_code: "ADMIN_OVERRIDE",
        trigger: "admin_open_level_override",
        ongoing_balance: 0.12,
    }),
    event(127, {
        ...admin,
        event_type: "RESTRICTION_LEVEL_CHANGED",
        old_value: "LIMITED",
        new_value: "CLEAR",
        reason_code: "ADMIN_OVERRIDE",
        trigger: "admin_open_level_override",
        ongoing_balance: 0.12,
    }),
    event(131, {
        ...system,
        event_type: "BALANCE_BECAME_NEGATIVE",
        old_value: "0.120000",
        new_value: "-2.020000",
        trigger: "charging_subtotal",
        ongoing_balance: -2.02,
    }),
    event(150, {
        ...user,
        event_type: "RESTRICTION_LEVEL_CHANGED",
        old_value: "CLEAR",
        new_value: "FROZEN",
        reason_code: "NO_VALID_PAYMENT_METHOD",
        trigger: "payment_method",
        ongoing_balance: -14.63,
    }),
    event(151, {
        ...user,
        event_type: "RESTRICTION_LEVEL_CHANGED",
        old_value: "FROZEN",
        new_value: "CLEAR",
        reason_code: "POST_PAYMENT_DEFAULT",
        trigger: "payment_method",
        ongoing_balance: -14.63,
    }),
    event(160, {
        ...system,
        event_type: "BALANCE_BECAME_POSITIVE",
        old_value: "-14.630000",
        new_value: "85.370000",
        trigger: "topup",
        ongoing_balance: 85.37,
        details: { credit_reference: "ch_mock_5Kq2Lx8" },
    }),
    event(190, {
        ...system,
        event_type: "BALANCE_BECAME_NEGATIVE",
        old_value: "0.400000",
        new_value: "-1.990000",
        trigger: "invoice",
        ongoing_balance: -1.99,
    }),
    event(192, {
        ...admin,
        event_type: "BALANCE_BECAME_POSITIVE",
        old_value: "-1.990000",
        new_value: "8.010000",
        trigger: "admin_credit",
        ongoing_balance: 8.01,
    }),
    event(200, {
        ...system,
        event_type: "RESTRICTION_LEVEL_CHANGED",
        old_value: "CLEAR",
        new_value: "FROZEN",
        reason_code: "ACCOUNT_PENDING_VALIDATION",
        trigger: "kyc",
        ongoing_balance: 8.01,
    }),
    event(201, {
        ...system,
        event_type: "RESTRICTION_LEVEL_CHANGED",
        old_value: "FROZEN",
        new_value: "CLEAR",
        reason_code: "POST_PAYMENT_DEFAULT",
        trigger: "kyc",
        ongoing_balance: 8.01,
    }),
];

/** Newest first, like the API returns them */
export const billingStatusHistoryExample = [...chronologicalEvents].reverse();
